import { CaretDownIcon, PushPinIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormCheck } from 'react-bootstrap';

import { CaretUpDownButtons } from '@/core/CaretUpDownButtons';
import { Tip } from '@/core/Tooltip';
import { translate } from '@/i18n';

import { COLUMN_ACTIONS_KEY } from './constants';
import { TableFiltersMenu } from './TableFiltersMenu';
import {
  TableProps,
  Column,
  Sorting,
  PinnedColumns,
  PinnedOffsets,
} from './types';
import { getColumnPinKey, getId } from './utils';

import './TableHeader.scss';

interface TableHeaderProps {
  columns: Column[];
  onSortClick?(sorting: Sorting): void;
  currentSorting?: Sorting;
  expandableRow?: boolean;
  hideExpandToggle?: boolean;
  showActions?: boolean;
  rows: any[];
  enableMultiSelect?: boolean;
  onSelectAllRows?(rows: any[]): void;
  selectedRows?: any[];
  toggled?: TableProps['toggled'];
  toggleRow?: TableProps['toggleRow'];
  fieldType?: TableProps['fieldType'];
  activeColumns?: Record<string, boolean>;
  filters?: TableProps['filters'];
  filtersStorage?: TableProps['filtersStorage'];
  setFilter?: TableProps['setFilter'];
  applyFiltersFn?: TableProps['applyFiltersFn'];
  columnPositions: string[];
  hasOptionalColumns?: boolean;
  equalColWidth?: boolean;
  toggleFilterMenu(show?): void;
  pinnedColumns?: PinnedColumns;
  pinnedOffsets?: PinnedOffsets;
  toggleColumnPin?(id: string): void;
}

function renderSortingIcon(
  column: Column,
  sorting: Sorting,
  sort: TableHeaderProps['onSortClick'],
) {
  if (!column.orderField || !sorting) {
    return null;
  }
  const onClickSort = (mode: Sorting['mode']) =>
    (column.orderField !== sorting.field || sorting.mode !== mode) &&
    sort({ field: column.orderField, mode });

  return (
    <span>
      <CaretUpDownButtons
        className="sorting-buttons"
        onClickUp={() => onClickSort('asc')}
        onClickDown={() => onClickSort('desc')}
        upClassName={
          column.orderField === sorting.field &&
          sorting.mode === 'asc' &&
          'active'
        }
        downClassName={
          column.orderField === sorting.field &&
          sorting.mode === 'desc' &&
          'active'
        }
        upTestId="sort-asc"
        downTestId="sort-desc"
      />
    </span>
  );
}

const WithThMeta = (children, meta) =>
  meta ? (
    <div className="th-wrapper">
      {children}
      {meta}
    </div>
  ) : (
    children
  );

const TableTh = ({
  column,
  onSortClick,
  currentSorting,
  filters,
  filtersStorage,
  setFilter,
  applyFiltersFn,
  toggleFilterMenu,
  isFirstDataColumn,
  expandableRow,
  hideExpandToggle,
  toggledAll,
  toggleAll,
  hasLeadingCheckbox,
  style,
  pinKey,
  pinned,
  shadow,
  pinOffset,
  onTogglePin,
}) => (
  <th
    className={
      classNames(
        column.orderField && 'sorting-column',
        column.filter && filters && 'filter-column',
        isFirstDataColumn && expandableRow && 'first-expandable-column',
        pinned && 'pinned pinned-start',
        pinned && shadow === 'end' && 'shadow-end',
        pinned && shadow === 'start' && 'shadow-start',
      ) || undefined
    }
    style={
      pinned && pinOffset
        ? { ...style, left: pinOffset.left, right: pinOffset.right }
        : style
    }
    data-pin-key={pinKey}
  >
    <div className="th-content">
      <div className="th-main">
        {WithThMeta(
          <>
            <span
              className={
                isFirstDataColumn && expandableRow
                  ? 'cell-with-expander-header'
                  : undefined
              }
              style={
                isFirstDataColumn && expandableRow
                  ? {
                      paddingLeft: hasLeadingCheckbox ? 12 : 16,
                      paddingRight: 16,
                    }
                  : undefined
              }
            >
              {isFirstDataColumn && expandableRow && !hideExpandToggle && (
                <span
                  className={classNames(
                    'all-rows-expander',
                    toggledAll ? 'active' : '',
                  )}
                  role="button"
                  tabIndex={0}
                  aria-label={
                    toggledAll
                      ? translate('Collapse all rows')
                      : translate('Expand all rows')
                  }
                  onClick={toggleAll}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleAll();
                    }
                  }}
                >
                  <CaretDownIcon
                    size={20}
                    weight="bold"
                    className="rotate-toggle-180"
                  />
                </span>
              )}
              {column.title}
              {renderSortingIcon(column, currentSorting, onSortClick)}
            </span>
            {column.filter && filters && (
              <TableFiltersMenu
                filters={filters}
                filterPosition="menu"
                filtersStorage={filtersStorage}
                setFilter={setFilter}
                applyFiltersFn={applyFiltersFn}
                openName={column.filter}
                toggleFilterMenu={toggleFilterMenu}
              />
            )}
          </>,
          column.meta,
        )}
      </div>
      {onTogglePin && (
        <button
          type="button"
          className={classNames('text-btn pin-toggle', pinned && 'active')}
          data-testid={`pin-toggle-${pinKey}`}
          aria-label={
            pinned ? translate('Unpin column') : translate('Pin column')
          }
          onClick={() => onTogglePin(pinKey)}
        >
          <Tip
            id={`tip-pin-${pinKey}`}
            label={pinned ? translate('Unpin column') : translate('Pin column')}
          >
            <PushPinIcon size={16} weight={pinned ? 'fill' : 'bold'} />
          </Tip>
        </button>
      )}
    </div>
  </th>
);

export const TableHeader: FC<TableHeaderProps> = ({
  columns,
  columnPositions,
  onSortClick,
  currentSorting,
  expandableRow = false,
  hideExpandToggle = false,
  showActions,
  rows,
  enableMultiSelect,
  onSelectAllRows,
  selectedRows,
  toggled,
  toggleRow,
  fieldType,
  filters,
  filtersStorage,
  setFilter,
  applyFiltersFn,
  hasOptionalColumns,
  equalColWidth,
  toggleFilterMenu,
  pinnedColumns = {},
  pinnedOffsets = {},
  toggleColumnPin,
}) => {
  const isAllSelected = selectedRows?.length >= rows?.length;

  const columnMap = useMemo(
    () =>
      columns.reduce(
        (result, column) => ({ ...result, [column.id]: column }),
        {},
      ),
    [columns],
  );

  const refCheck = useRef<HTMLInputElement>();
  useEffect(() => {
    if (refCheck?.current) {
      refCheck.current.indeterminate =
        !isAllSelected && selectedRows?.length > 0;
    }
  }, [refCheck?.current, isAllSelected, selectedRows]);

  const toggledAll = useMemo(() => {
    if (!expandableRow || rows.length === 0) return false;
    return rows.every((row, i) => toggled[getId(row, i)]);
  }, [toggled, getId, rows, expandableRow]);

  const toggleAll = useCallback(() => {
    if (toggledAll) {
      rows.forEach((row, i) => {
        toggleRow(getId(row, i));
      });
    } else {
      rows.forEach((row, i) => {
        const rowId = getId(row, i);
        if (!toggled[rowId]) toggleRow(rowId);
      });
    }
  }, [rows, toggledAll, toggled, toggleRow]);

  const visibleCols = useMemo<Column[]>(() => {
    return hasOptionalColumns
      ? columnPositions
          .filter((id) => columnMap[id] && (columnMap[id].visible ?? true))
          .map((id) => columnMap[id])
      : columns.filter((column) => column.visible ?? true);
  }, [hasOptionalColumns, columnPositions, columnMap, columns]);

  const colWidths = useMemo(() => {
    if (visibleCols.length <= 1) return { first: 100, other: 0 };
    const firstColMul = equalColWidth ? 1 : 2;
    const colsWithoutCustomWidth = visibleCols.filter(
      (col) => !col.width,
    ).length;
    const first = Math.min(50, (100 / colsWithoutCustomWidth) * firstColMul);
    const remainingWidth = 100 - first;
    const other = remainingWidth / (colsWithoutCustomWidth - 1);
    return { first, other };
  }, [visibleCols]);

  // The first column which has no custom width. Find it to make it wider.
  const firstColIndex = visibleCols.findIndex((col) => !col.width);
  const hasLeadingCheckbox = Boolean(fieldType || enableMultiSelect);

  const firstVisibleDataIndex = hasOptionalColumns
    ? columnPositions
        .filter((id) => columnMap[id])
        .findIndex((id) => columnMap[id].visible ?? true)
    : columns.findIndex((col) => col.visible ?? true);

  return (
    <>
      <colgroup>
        {fieldType || enableMultiSelect ? <col width="10px" /> : null}
        {visibleCols.map((col, i) => (
          <col
            key={i}
            style={
              col.width
                ? { width: col.width }
                : {
                    width:
                      (i === firstColIndex
                        ? colWidths.first
                        : colWidths.other) + '%',
                    minWidth: i === firstColIndex ? 200 : 150,
                  }
            }
          />
        ))}
      </colgroup>
      <thead>
        <tr className="text-start text-muted fw-bolder fs-7 gs-0 align-middle">
          {fieldType ? (
            <th
              style={{ width: '10px', paddingLeft: '16px', paddingRight: 0 }}
            />
          ) : enableMultiSelect ? (
            <th style={{ width: '10px', paddingLeft: '16px', paddingRight: 0 }}>
              <FormCheck
                ref={refCheck}
                data-testid="select-all"
                className="form-check form-check-custom form-check-md"
                checked={isAllSelected}
                onChange={() => onSelectAllRows(rows)}
              />
            </th>
          ) : null}
          {hasOptionalColumns
            ? columnPositions
                .filter((id) => columnMap[id])
                .map(
                  (id, index) =>
                    (columnMap[id].visible ?? true) && (
                      <TableTh
                        key={id}
                        column={columnMap[id]}
                        onSortClick={onSortClick}
                        currentSorting={currentSorting}
                        filters={filters}
                        filtersStorage={filtersStorage}
                        setFilter={setFilter}
                        applyFiltersFn={applyFiltersFn}
                        toggleFilterMenu={toggleFilterMenu}
                        isFirstDataColumn={index === firstVisibleDataIndex}
                        expandableRow={expandableRow}
                        hideExpandToggle={hideExpandToggle}
                        toggledAll={toggledAll}
                        toggleAll={toggleAll}
                        hasLeadingCheckbox={hasLeadingCheckbox}
                        pinKey={id}
                        pinned={id in pinnedColumns}
                        shadow={pinnedColumns[id]}
                        pinOffset={pinnedOffsets[id]}
                        onTogglePin={toggleColumnPin}
                        style={
                          expandableRow && index === firstVisibleDataIndex
                            ? { paddingLeft: 0, paddingRight: 0 }
                            : undefined
                        }
                      />
                    ),
                )
            : columns.map((column, index) => {
                if (!(column.visible ?? true)) {
                  return false;
                }
                const pinKey = getColumnPinKey(column, index);
                return (
                  <TableTh
                    key={index}
                    column={column}
                    onSortClick={onSortClick}
                    currentSorting={currentSorting}
                    filters={filters}
                    filtersStorage={filtersStorage}
                    setFilter={setFilter}
                    applyFiltersFn={applyFiltersFn}
                    toggleFilterMenu={toggleFilterMenu}
                    isFirstDataColumn={index === firstVisibleDataIndex}
                    expandableRow={expandableRow}
                    hideExpandToggle={hideExpandToggle}
                    toggledAll={toggledAll}
                    toggleAll={toggleAll}
                    hasLeadingCheckbox={hasLeadingCheckbox}
                    pinKey={pinKey}
                    pinned={pinKey in pinnedColumns}
                    shadow={pinnedColumns[pinKey]}
                    pinOffset={pinnedOffsets[pinKey]}
                    onTogglePin={toggleColumnPin}
                    style={
                      expandableRow && index === firstVisibleDataIndex
                        ? { paddingLeft: 0, paddingRight: 0 }
                        : undefined
                    }
                  />
                );
              })}
          {showActions ? (
            <th
              className={classNames(
                'header-actions',
                COLUMN_ACTIONS_KEY in pinnedColumns && 'pinned pinned-end',
                pinnedColumns[COLUMN_ACTIONS_KEY] && 'is-floating',
              )}
            >
              {translate('Actions')}
            </th>
          ) : null}
        </tr>
      </thead>
    </>
  );
};
